"use client";

import clsx from "clsx";
import { Loader2 } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Control, Controller, Path } from "react-hook-form";
import { components, ControlProps, GroupBase, MenuListProps, OptionsOrGroups } from "react-select";
import AsyncSelect from "react-select/async";

type SelectControlProps<T> = ControlProps<
  {
    value: T;
    label: string;
  },
  false,
  GroupBase<{ value: T; label: string }>
>;

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
  };
}

const CustomLoadingIndicator = () => (
  <div className="flex items-center justify-center px-2">
    <Loader2 className="h-4 w-4 animate-spin text-primary" />
  </div>
);

const LoadingMore = () => (
  <div className="flex items-center justify-center p-2 text-sm text-gray-500">
    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    Loading more...
  </div>
);

export interface Option {
  label: string;
  value: any;
}

interface FormAutoCompleteProps<T extends Record<string, any>> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  loadOptions?: (
    inputValue: string,
    page?: number,
    filter?: Record<string, string>,
  ) => Promise<PaginatedResponse<Option>>;

  displayOption?: (option: any) => Option;
  transformer?: (value: any) => any;
  onSelect?: (option: Option) => void;
  onClear?: () => void;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  defaultOptions?: boolean | readonly Option[];
  customNoOptionsMessage?: () => string;
  dependencies?: Record<string, any>;
  shouldResetOnDependencyChange?: boolean;
  hideIfNoData?: boolean;
}

const FormAutoComplete = <T extends Record<string, any>>({
  name,
  control,
  label,
  loadOptions: loadOptionsProp = async () => ({
    data: [],
    meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
  }),
  displayOption,
  transformer,
  onSelect,
  onClear,
  placeholder,
  isDisabled = false,
  isClearable = true,
  defaultOptions = true,
  customNoOptionsMessage,
  dependencies = {},
  shouldResetOnDependencyChange = true,
  hideIfNoData,
  ...props
}: FormAutoCompleteProps<T>) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentOptions, setCurrentOptions] = useState<Option[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [initialOptionsLoaded, setInitialOptionsLoaded] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false); // Track if user is actively typing

  // Add debounce ref to prevent multiple requests
  const isLoadingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const prevDependenciesRef = useRef(dependencies);

  // Memoize default functions to prevent unnecessary re-renders
  const defaultDisplayOption = useCallback(
    (option: any): Option | null => {
      if (!option) return null;

      if (option?.label) {
        return option;
      }

      // If just a value is provided, try to find its label in current options
      const foundOption = currentOptions.find((o: any) => o.value === option);

      return {
        label: foundOption?.label || String(option),
        value: option.value !== undefined ? option.value : option,
      };
    },
    [currentOptions],
  );

  const getOptionDisplay = displayOption || defaultDisplayOption;
  const transformValue = useCallback(transformer || ((value: any): any => value), []);

  // Load initial options if defaultOptions is true
  useEffect(() => {
    const fetchInitialOptions = async () => {
      if (defaultOptions === true && !initialOptionsLoaded) {
        try {
          setIsLoading(true);
          const response = await loadOptionsProp("", 1, dependencies);
          setCurrentOptions(response.data);
          setHasNextPage(response.meta.hasNextPage);
          setInitialOptionsLoaded(true);
        } catch (error) {
          console.error("Error loading initial options:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchInitialOptions();
  }, [JSON.stringify(dependencies), defaultOptions, initialOptionsLoaded, loadOptionsProp]);

  // Watch for dependency changes
  useEffect(() => {
    const currentDepsJson = JSON.stringify(dependencies);
    const prevDepsJson = JSON.stringify(prevDependenciesRef.current);
    const dependenciesChanged = currentDepsJson !== prevDepsJson;

    if (dependenciesChanged && shouldResetOnDependencyChange) {
      // Reset options and refetch with new dependencies
      setCurrentOptions([]);
      setInitialOptionsLoaded(false);
      setIsUserTyping(false); // Reset typing state on dependency change
      prevDependenciesRef.current = dependencies;

      // Immediately fetch new options with the updated dependencies if defaultOptions is true
      if (defaultOptions === true) {
        const fetchOptionsWithNewDeps = async () => {
          try {
            setIsLoading(true);
            const response = await loadOptionsProp("", 1, dependencies);
            setCurrentOptions(response.data);
            setHasNextPage(response.meta.hasNextPage);
            setInitialOptionsLoaded(true);
          } catch (error) {
            console.error("Error loading options after dependency change:", error);
          } finally {
            setIsLoading(false);
          }
        };

        fetchOptionsWithNewDeps();
      }
    }
  }, [JSON.stringify(dependencies), shouldResetOnDependencyChange, defaultOptions, loadOptionsProp]);

  // Optimized load options wrapper with request cancellation
  const loadOptionsWrapper = useCallback(
    async (inputValue: string): Promise<OptionsOrGroups<Option, GroupBase<Option>>> => {
      if (isLoadingRef.current) {
        abortControllerRef.current?.abort();
      }

      abortControllerRef.current = new AbortController();
      isLoadingRef.current = true;
      setIsLoading(true);
      setSearchQuery(inputValue);

      // Only set typing state if user actually typed something
      if (inputValue.length > 0) {
        setIsUserTyping(true);
      }

      try {
        const response = await loadOptionsProp(inputValue, 1, dependencies);
        if (!abortControllerRef.current.signal.aborted) {
          setCurrentOptions(response.data);
          setCurrentPage(1);
          setHasNextPage(response.meta.hasNextPage);
          return response.data;
        }
        return [];
      } catch (error) {
        if (!abortControllerRef.current.signal.aborted) {
          console.error("Error loading options:", error);
        }
        return [];
      } finally {
        if (!abortControllerRef.current.signal.aborted) {
          setIsLoading(false);
          isLoadingRef.current = false;
        }
      }
    },
    [dependencies, loadOptionsProp],
  );

  // Optimized load more options
  const loadMoreOptions = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || isLoadingRef.current) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await loadOptionsProp(searchQuery, nextPage, dependencies);

      setCurrentOptions((prev) => [...prev, ...response.data]);
      setCurrentPage(nextPage);
      setHasNextPage(response.meta.hasNextPage);
    } catch (error) {
      console.error("Error loading more options:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, hasNextPage, isLoadingMore, searchQuery, dependencies, loadOptionsProp]);

  const CustomMenuList = ({ children, ...props }: MenuListProps<Option, false>) => {
    const menuRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        const element = e.target as HTMLDivElement;
        if (element.scrollHeight - element.scrollTop <= element.clientHeight + 50 && hasNextPage && !isLoadingMore) {
          loadMoreOptions();
        }
      },
      [hasNextPage, isLoadingMore],
    );

    return (
      <components.MenuList {...props} innerRef={menuRef}>
        <div className={classNames.menuList()} onScroll={handleScroll}>
          {children}
          {isLoadingMore && <LoadingMore />}
        </div>
      </components.MenuList>
    );
  };

  const classNames = {
    control: (state: SelectControlProps<any>) =>
      clsx(
        "flex items-center bg-secondary-light border border-secondary-light rounded-2xl min-h-[42px] hover:cursor-pointer hover:border-grey-border p-3 transition-all duration-300",
        state.isFocused ? "border-grey-border ring-1 ring-grey-border" : "outline-gray-300",
      ),
    valueContainer: () => "flex flex-wrap flex-1 px-3 py-1 gap-1",
    placeholder: () => "inter text-secondary-foreground text-base",
    input: () => "text-primary-foreground font-meduim text-base inter focus:outline-none p-0 m-0",
    menu: () => "mt-1 mb-2 border border-secondary-light rounded-2xl bg-white shadow-lg absolute w-full z-50",
    menuList: () => "max-h-60 inter font-medium overflow-auto",
    option: ({ isSelected, isFocused }: { isSelected: boolean; isFocused: boolean }) =>
      clsx(
        "px-4 py-2 cursor-pointer inter font-medium first:rounded-t-2xl last:rounded-b-2xl",
        isSelected && "bg-secondary-light text-black rounded-xl",
        !isSelected && isFocused && "bg-secondary-light text-black overflow-hidden",
        !isSelected && !isFocused && "text-gray-800 hover:bg-gray-50",
      ),
    singleValue: () => "text-[#000000] inter font-medium",
    indicatorsContainer: () => "flex items-center px-2",
    dropdownIndicator: (state: { selectProps: { menuIsOpen: boolean } }) =>
      clsx(
        "text-gray-400 hover:text-gray-600 transition-transform duration-300",
        state.selectProps.menuIsOpen && "rotate-180 transition-transform duration-300",
      ),
    clearIndicator: () => "text-primary p-1",
    indicatorSeparator: () => "hidden",
    loadingMessage: () => "text-primary px-2 py-3",
    noOptionsMessage: () => "text-secondary-foreground p-4",
  };

  // Updated hide condition: hide if no data, not loading, hideIfNoData is true,
  // and user is not actively typing
  if (!currentOptions?.length && !isLoading && hideIfNoData && !isUserTyping) {
    return null;
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState: { isSubmitting }, fieldState: { error, isTouched } }) => {
        return (
          <div className="w-full">
            {label && <label className="inter mb-1 block text-sm font-semibold text-[#000000]">{label}</label>}
            <AsyncSelect
              key={`${name}-${JSON.stringify(dependencies)}`}
              instanceId={`${name}-${JSON.stringify(dependencies)}`}
              unstyled
              cacheOptions={false}
              defaultOptions={defaultOptions}
              loadOptions={loadOptionsWrapper}
              value={field.value ? getOptionDisplay(field.value) : null}
              onChange={(selectedOption: Option | null) => {
                if (selectedOption) {
                  const transformedValue = transformValue(selectedOption.value);
                  field.onChange(transformedValue);
                  onSelect?.(selectedOption);
                } else {
                  field.onChange(null);
                  onClear?.();
                }
              }}
              onBlur={field.onBlur}
              isDisabled={isDisabled}
              isClearable={isClearable}
              placeholder={placeholder}
              classNames={classNames}
              components={{
                ...components,
                LoadingIndicator: CustomLoadingIndicator,
                MenuList: CustomMenuList,
                NoOptionsMessage: customNoOptionsMessage
                  ? () => <div className={classNames.noOptionsMessage()}>{customNoOptionsMessage()}</div>
                  : components.NoOptionsMessage,
              }}
              isLoading={isLoading}
              {...props}
            />
            {error && <p className="inter mt-2 text-sm font-semibold text-red-500 rtl:text-right">{error.message}</p>}
          </div>
        );
      }}
    />
  );
};

export default FormAutoComplete;
